import type { Order } from "@estelle/domain";

/**
 * How loudly an order's row speaks (#643): show the exception, mute the
 * normal.
 *
 * - `urgent` (red edge, bold): a New COD order past the overdue limit, a
 *   parcel coming back, a lost one.
 * - `warn` (amber edge, bold): a delivery attempt that failed.
 * - `ready` (blue edge, bold): Confirmed, waiting to ship.
 * - `quiet` (muted): Delivered, Cancelled, RTO received — nothing left to do.
 * - `null`: everything in between reads as it always has.
 */
export type OrderEmphasis = "urgent" | "warn" | "ready" | "quiet" | null;

export function orderEmphasis(
  order: Pick<Order, "stage" | "overdueHours">,
): OrderEmphasis {
  switch (order.stage) {
    case "new":
      return order.overdueHours !== null ? "urgent" : null;
    case "returning":
    case "lost":
      return "urgent";
    case "attempt_failed":
      return "warn";
    case "confirmed":
      return "ready";
    case "delivered":
    case "cancelled":
    case "rto_received":
      return "quiet";
    default:
      return null;
  }
}

/** The row's classes for an emphasis: the edge is drawn on the first cell. */
export const EMPHASIS_CLASS: Record<Exclude<OrderEmphasis, null>, string> = {
  urgent:
    "font-semibold [&>td:first-child]:shadow-[inset_3px_0_0_var(--color-destructive)] max-md:shadow-[inset_3px_0_0_var(--color-destructive)]",
  warn: "font-semibold [&>td:first-child]:shadow-[inset_3px_0_0_var(--color-tint-yellow-foreground)] max-md:shadow-[inset_3px_0_0_var(--color-tint-yellow-foreground)]",
  ready:
    "font-semibold [&>td:first-child]:shadow-[inset_3px_0_0_var(--color-tint-blue-foreground)] max-md:shadow-[inset_3px_0_0_var(--color-tint-blue-foreground)]",
  quiet: "text-muted-foreground",
};
